import { Trans, useTranslation } from "react-i18next";
import {
  Checkbox,
  Combobox,
  ComboboxItem,
  InputBase,
  SelectProps,
  Text,
  useCombobox,
} from "@mantine/core";
import { modals } from "@mantine/modals";

import {
  showLoadingNotification,
  showSuccessNotification,
} from "@utils/showNotification";

import { useUpdateBillServiceStatus } from "@hooks/mutations/billing/useUpdateBillServiceStatus";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";


interface StatusSelectProps {
  value: string;
  setValue: (arg: string) => void;
  data: ComboboxItem[];
  consultationId: string;
  serviceId: string;
  billId: string;
  refetchBill: () => void;
}

const billingStatusColors: any = {
  pending: "yellow",
  paid: "green",
  accepted: "green",
  rejected: "red",
};

const getConfirmationText = (value: string) => {
  if (value === "accepted") {
    return (
      <Trans
        i18nKey={"bill-service-status-confirmation.accepted"}
        components={{
          bold: <Text span inherit fw={600} />,
          p: <Text inherit lh={1.5} />,
        }}
      />
    );
  } else if (value === "rejected") {
    return (
      <Trans
        i18nKey={"bill-service-status-confirmation.rejected"}
        components={{
          bold: <Text span inherit fw={600} />,
          p: <Text inherit lh={1.5} />,
        }}
      />
    );
  }
};

export const StatusSelect = ({
  value,
  setValue,
  data,
  consultationId,
  serviceId,
  billId,
  refetchBill,
}: StatusSelectProps) => {
  const { t } = useTranslation();
  const { mutate: updateStatus, isLoading } = useUpdateBillServiceStatus();
  const handleErrors = useQueryErrorHandling();

  return (
    <CustomSelect
      disabled={isLoading}
      styles={{
        input: {
          lineHeight: 1.3,
          minHeight: 22,
          height: 22,
          backgroundColor: `var(--${billingStatusColors[value as string]}-3)`,
          color: `var(--${billingStatusColors[value as string]}-11) !important`,
        },
      }}
      value={value}
      onChange={val => {
        if (!val) return;

        modals.openConfirmModal({
          title: <Text fw={500}>{t("confirm-status-change")}</Text>,
          centered: true,
          children: (
            <>
              <Text lh={1.5} fz={15}>
                {getConfirmationText(val as string)}
              </Text>

              <Checkbox
                defaultChecked={true}
                checked={true}
                mt="1rem"
                label={t("patient-confirmation-approval")}
              />
            </>
          ),
          labels: { confirm: t("confirm"), cancel: t("cancel") },
          groupProps: { gap: "xs", mt: "lg" },
          onCancel: () => {
            // Do something here...
            return;
          },
          onConfirm: () => {
            const id = showLoadingNotification({});
            updateStatus(
              {
                billId,
                consultationId,
                serviceId,
                status: val,
              },
              {
                onSuccess: () => {
                  showSuccessNotification({
                    id,
                    title: "success",
                    message: "update-bill-service-status-success",
                  });
                  setValue(val as string);
                  refetchBill();
                },
                onError: (res: any) => {
                  handleErrors(res, "mutation", id);
                },
              }
            );
          },
          styles: { body: { minHeight: "auto" } },
        });
      }}
      data={data}
      size="xs"
      comboboxProps={{
        width: "fit-content",
        position: "bottom-start",
        offset: 5,
      }}
    />
  );
};

interface CustomSelectProps extends SelectProps {
  data: ComboboxItem[];
}

function CustomSelect({
  data = [],
  value,
  onChange,
  disabled,
  size = "xs",
  styles,
  comboboxProps,
}: CustomSelectProps) {
  const { t } = useTranslation();
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  // Filter out disabled options for display in dropdown
  const selectableOptions = data.filter(item => !item.disabled);

  // Find the current value's label (even if it's disabled)
  const currentOption = data.find(item => item.value === value) as ComboboxItem;
  const displayValue = currentOption?.label || "";
  const isPaid = value === "paid";

  const options = selectableOptions.map(item => (
    <Combobox.Option value={item.value} key={item.value} fz={13}>
      {item.label}
    </Combobox.Option>
  ));

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={val => {
        onChange?.(val, currentOption);
        combobox.closeDropdown();
      }}
      {...comboboxProps}>
      <Combobox.Target>
        <InputBase
          component="button"
          type="button"
          pointer={!isPaid}
          rightSection={isPaid ? null : <Combobox.Chevron />}
          rightSectionPointerEvents="none"
          onClick={() => {
            if (isPaid) return;
            combobox.toggleDropdown();
          }}
          disabled={disabled}
          size={size}
          styles={styles}>
          {displayValue || ""}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {options.length > 0 ? (
            options
          ) : (
            <Combobox.Empty>{t("empty-data")}</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
